import { max } from 'd3-array';
import { axisLeft } from 'd3-axis';
import { color } from 'd3-color';
import { scaleBand, scaleLinear } from 'd3-scale';
import { Component } from 'react';
import styled from 'styled-components';
import Axis from '../../../../../generic/charts/axis';
import { getTextStyle } from '../../../../../generic/content/texts/bou-text/text-style';
import { BouColor } from '../../../../../generic/global/bou-colors';
import responsive, { ResponsiveProps } from '../../../../../generic/layout/responsive';
import { formatPercentage } from '../../../../../generic/utilities/format';
import { simpleRatings } from './rating-scale';

const SVG = styled.svg`
  overflow: visible !important;
  line {
    stroke-width: 1px;
    stroke: ${BouColor.TOOL_TEXT_DARK_GRAY};
  }
  path {
    stroke-width: 1px;
    stroke: ${BouColor.TOOL_TEXT_DARK_GRAY};
  }
  text {
    font-size: 14px;
  }
`;

const AxisLabel = styled.text`
  ${getTextStyle({ size: 'xxxxs' })};
  fill: black;
`;

const BarLabel = styled.text``;

const Rect = styled.rect``;

const EventRect = styled.rect`
  cursor: pointer;
  opacity: 0;
`;

export interface RatingsHistogramDatum {
  weight: number;
  rating: string;
}

interface PassedProps {
  height?: number;
  data: RatingsHistogramDatum[];
  labelFormatter: (values: number[], index: number) => string;
  marginLeft: number;
  marginRight: number;
  marginTop: number;
  marginBottom: number;
}

type Props = PassedProps & ResponsiveProps;

function getRectColor(isOwn: boolean, hovered: boolean) {
  if (isOwn) {
    return hovered
      ? color(BouColor.TOOL_HISTOGRAM_HIGHLIGHT_BLUE)?.darker(0.3).hex()
      : BouColor.TOOL_HISTOGRAM_HIGHLIGHT_BLUE;
  }
  return hovered ? color(BouColor.GRAY)!.darker(0.3).hex() : color(BouColor.GRAY)!.darker(0.1).hex();
}

interface State {
  hoveredLower: number | undefined;
  hoveredHigher: number | undefined;
}

class HistogramPlain extends Component<Props, State> {
  public state: State = {
    hoveredLower: undefined,
    hoveredHigher: undefined,
  };

  private getXScale() {
    const { width, marginLeft, marginRight } = this.props;
    const chartWidth = width ? width - marginLeft - marginRight : 0;
    const xScale = scaleBand().domain(simpleRatings).rangeRound([0, chartWidth]).padding(0.1);
    return xScale;
  }

  private getWidth() {
    return this.props.width || 500;
  }

  public render() {
    const { data, width, marginLeft, marginTop, marginBottom, marginRight } = this.props;

    if (data.length === 0) {
      return null;
    }

    function yTickFormatter(value: number) {
      return formatPercentage(value / data.length);
    }

    const heightRatio = 1 / 1.619;
    const height = this.props.height || heightRatio * this.getWidth();
    const chartHeight = height - marginTop - marginBottom;
    const chartWidth = this.getWidth() - marginLeft - marginRight;

    const xScale = this.getXScale();

    const maxY = max(data, d => d.weight)!;
    const yScale = scaleLinear()
      .domain([0, maxY * 1.1])
      .range([chartHeight, 0]);

    const barWidth = xScale.bandwidth();
    return (
      <SVG
        width={width}
        height={height}
        onMouseLeave={() => {
          this.setState({ hoveredHigher: undefined, hoveredLower: undefined });
        }}
      >
        <g transform={`translate(${marginLeft},${marginTop})`}>
          {data.map((d, i) => {
            const isOwn = false;
            const x = xScale(d.rating);
            const y = yScale(d.weight);
            const hovered = false;
            const rectColor = getRectColor(isOwn, hovered);
            const barHeight = chartHeight - (yScale(d.weight) ?? 0);
            return (
              <g key={`bar-${i}`} transform={`translate(${x},${y})`}>
                <Rect
                  x={1}
                  width={barWidth}
                  stroke="transparent"
                  strokeWidth={1}
                  fill={rectColor}
                  height={barHeight}
                  rx={2}
                  ry={2}
                />
                <g transform={`translate(${barWidth / 2}, 0)`}>
                  <BarLabel dy="-1em" textAnchor="middle">
                    {(d.weight * 100).toFixed(1)} %
                  </BarLabel>
                </g>
                <g transform={`translate(${barWidth / 2}, ${barHeight})`}>
                  <BarLabel textAnchor="middle" dy="1.5em">
                    {d.rating}
                  </BarLabel>
                </g>
              </g>
            );
          })}
          {data.map((d, i) => {
            const x = xScale(d.rating);
            const y = yScale(d.weight);
            return (
              <g key={`bar-${i}`} transform={`translate(${x},${y})`}>
                <EventRect
                  x={0}
                  width={barWidth + 1}
                  height={chartHeight}
                  rx={2}
                  ry={2}
                  onMouseEnter={() => undefined}
                />
              </g>
            );
          })}
          <Axis
            transform={`translate(0,0)`}
            axis={axisLeft<number>(yScale).tickFormat(yTickFormatter).tickValues([])}
            axisType="left"
            tickFormatter={yTickFormatter}
          />
          <g id="x-axis" transform={`translate(0,${chartHeight})`}>
            <line x1={0} x2={chartWidth} y1={0} y2={0} stroke="red" />
            <AxisLabel id="x-axis-label" textAnchor="end" x={chartWidth} y="3.25em">
              Net impact rating
            </AxisLabel>
          </g>
          <g id="y-axis">
            <AxisLabel id="y-axis-label" textAnchor="end" x={0} y="-1.25em" transform="rotate(-90)">
              Weight
            </AxisLabel>
          </g>
        </g>
      </SVG>
    );
  }
}

export const Histogram = responsive(HistogramPlain);
